import React, { useState, useEffect } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { useTranslation } from "react-i18next";
import { AiFillEdit, AiFillDelete } from "react-icons/ai";
import useToastContext from "hooks/useToastContext";
import helpers from "utils/helpers";

const TemplateManage = ({ show, handleClose, locations }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [templates, setTemplates] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    startTime: "",
    endTime: "",
    breakDuration: 60,
    locationId: "",
  });

  const getTemplates = async () => {
    try {
      const res = await apiGet(apiPath.getShiftTemplates, {});
      setTemplates(res?.data?.results || []);
    } catch (error) {
      console.log("Error fetching templates:", error);
    }
  };

  useEffect(() => {
    if (show) {
      getTemplates();
      resetForm();
    }
  }, [show]);

  const resetForm = () => {
    setIsEditing(false);
    setIsAdding(false);
    setCurrentTemplate(null);
    setFormData({ name: "", startTime: "", endTime: "", breakDuration: 60, locationId: "" });
  };

  const handleAddNew = () => {
    resetForm();
    setIsAdding(true);
  };

  const handleEditTemplate = (template) => {
    setIsAdding(false);
    setIsEditing(true);
    setCurrentTemplate(template);
    setFormData({
      name: template.name,
      startTime: template.startTime,
      endTime: template.endTime,
      breakDuration: template.breakDuration,
      locationId: template.locationId?._id || template.locationId || "",
    });
  };

  const handleDeleteTemplate = async (templateId) => {
    helpers.alertFunction(
      t("CONFIRM_DELETE_TEMPLATE"),
      templateId,
      async (id) => {
        try {
          const res = await apiDelete(`${apiPath.deleteShiftTemplate}/${id}`);
          if (res?.data?.success) {
            notification.success(res?.data?.message);
            getTemplates();
          } else {
            notification.error(res?.data?.message);
          }
        } catch (error) {
          notification.error(t("SOMETHING_WENT_WRONG"));
        }
      },
      true
    );
  };

  const handleSave = async () => {
    if (!formData.name || !formData.startTime || !formData.endTime || !formData.locationId) {
      notification.error(t("PLEASE_FILL_ALL_FIELDS"));
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        ...formData,
        breakDuration: Number(formData.breakDuration),
      };

      let res;
      if (isEditing && currentTemplate?._id) {
        // Update existing template
        res = await apiPut(`${apiPath.updateShiftTemplate}/${currentTemplate._id}`, payload);
      } else {
        // Create new template
        res = await apiPost(apiPath.createShiftTemplate, payload);
      }

      if (res?.data?.success) {
        notification.success(res?.data?.message);
        getTemplates();
        resetForm();
      } else {
        notification.error(res?.data?.message);
      }
    } catch (error) {
      notification.error(t("SOMETHING_WENT_WRONG"));
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black opacity-40" onClick={handleClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden outline-none pointer-events-none">
        <div className="relative w-full my-6 mx-auto max-w-3xl pointer-events-auto">
          <div className="border-0 rounded-xl shadow-xl relative flex flex-col w-full bg-white outline-none max-h-[85vh]">

            {/* Header */}
            <div className="p-4 flex items-center justify-between border-b border-solid border-slate-100 rounded-t">
              <h3 className="font-bold text-lg text-gray-800">{t("MANAGE_TEMPLATES")}</h3>
              <button type="button" onClick={handleClose} className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600">
                <span className="text-2xl font-light">×</span>
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto max-h-[65vh]">
              {/* Top Bar - Add Button */}
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-gray-700">
                  {t("EXISTING_TEMPLATES")} ({templates.length})
                </h4>
                <button
                  type="button"
                  onClick={handleAddNew}
                  className="bg-LightBlue text-white text-sm px-4 py-2 rounded-lg hover:bg-DarkBlue transition-colors flex items-center gap-1"
                >
                  + {t("ADD_TEMPLATE")}
                </button>
              </div>

              {/* Template List */}
              <div className="border rounded-lg overflow-hidden mb-4">
                <table className="w-full text-xs text-left text-[#A5A5A5]">
                  <thead className="text-gray-900 text-xs border-b bg-[#E1E6EE]">
                    <tr>
                      <th className="px-4 py-3">{t("TEMPLATE_NAME")}</th>
                      <th className="px-4 py-3">{t("START_TIME")}</th>
                      <th className="px-4 py-3">{t("END_TIME")}</th>
                      <th className="px-4 py-3">{t("BREAK")}</th>
                      <th className="px-4 py-3">{t("LOCATION")}</th>
                      <th className="px-4 py-3 text-center">{t("O_ACTION")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-400">
                          {t("NO_TEMPLATES_FOUND")}
                        </td>
                      </tr>
                    ) : (
                      templates.map((template) => (
                        <tr key={template._id} className="bg-white border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900 font-medium">{template.name}</td>
                          <td className="px-4 py-3 text-gray-600">{template.startTime}</td>
                          <td className="px-4 py-3 text-gray-600">{template.endTime}</td>
                          <td className="px-4 py-3 text-gray-600">{template.breakDuration} min</td>
                          <td className="px-4 py-3 text-gray-600">
                            {template.locationId?.name || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditTemplate(template)}
                                className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                                title={t("EDIT")}
                              >
                                <AiFillEdit className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteTemplate(template._id)}
                                className="p-1.5 hover:bg-red-50 rounded text-red-600"
                                title={t("DELETE")}
                              >
                                <AiFillDelete className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Add/Edit Form */}
              {(isAdding || isEditing) && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    {isEditing ? `${t("EDIT_TEMPLATE")}: ${currentTemplate?.name}` : t("ADD_NEW_TEMPLATE")}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
                    <div className="relative z-0 w-full group">
                      <input
                        type="text"
                        className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 peer"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder=" "
                      />
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("TEMPLATE_NAME")} <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="relative z-0 w-full group">
                      <select
                        className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
                        value={formData.locationId}
                        onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                      >
                        <option value="">{t("SELECT_LOCATION")}</option>
                        {locations?.map((loc) => (
                          <option key={loc._id} value={loc._id}>{loc.name}</option>
                        ))}
                      </select>
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("LOCATION")} <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="relative z-0 w-full group">
                      <input
                        type="time"
                        className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 peer"
                        value={formData.startTime}
                        onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      />
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("START_TIME")} <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="relative z-0 w-full group">
                      <input
                        type="time"
                        className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 peer"
                        value={formData.endTime}
                        onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      />
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("END_TIME")} <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="relative z-0 w-full group">
                      <input
                        type="number"
                        className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 peer"
                        value={formData.breakDuration}
                        onChange={(e) => setFormData({ ...formData, breakDuration: e.target.value })}
                        min="0"
                      />
                      <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                        {t("BREAK_DURATION_MIN")} <span className="text-red-500">*</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="text-gray-700 bg-gray-200 font-medium px-6 py-2 text-sm rounded-md hover:bg-gray-300"
                    >
                      {t("O_CANCEL")}
                    </button>
                    {isLoading ? (
                      <div className="flex justify-center bg-[#4F46E5] px-6 py-2 rounded-md">
                        <div className="loading-spinner h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSave}
                        className="bg-[#4F46E5] text-white font-medium px-6 py-2 text-sm rounded-md hover:bg-[#4338CA]"
                      >
                        {isEditing ? t("O_UPDATE") : t("O_ADD")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TemplateManage;